import DatePicker from 'src/components/DatePickers/Ranged';
import htmlParse from 'html-react-parser';
import moment from 'src/utils/moment';
import SVG from 'src/components/Images/SvgRenderer';
import useStyles from './style';
import { getSchoolSettings, handleSum, inflection } from 'src/utils/useFunctions';
import { getUserRole } from 'src/utils/useUser';
import { useAppSelector } from '../../../../../hooks/redux-hooks';
import { useCallback } from 'react';
import { useStates } from 'src/utils/useState';
import { useTranslation } from 'react-i18next';

const SubstitutionsValidator: React.FunctionComponent = () => {

  const { t } = useTranslation();
  const classes = useStyles();
  const dataData = useAppSelector((state: any) => state.data);
  const substitutionsData = useAppSelector((state: any) => state.substitutions);
  const userData = useAppSelector((state: any) => state.user);
  const childData = substitutionsData.childData;
  const curChild = substitutionsData.currentChild;
  const schoolID = curChild.schoolID;

  const startDate = moment(childData.firstDay).add(1, "day");
  const lastDate = moment().add(childData.schoolDays, "days");

  const [state, setState] = useStates({
    selectedDate: null,
  });

  const handleDate = (date: any) => {
    setState("selectedDate", date);
  };

  const parseDay = (day: any) => {
    if(day === 0) return 7;
    else return day;
  };

  const getChildData = useCallback((childID: any) => {
    return dataData.children.filter((item: any) => item.childID === childID).length === 0 ? [] : dataData.children.find((item: any) => item.childID === childID);
  }, [dataData.children]);

  const getDateCredit = useCallback((date: any) => {
    const allDays = childData.allDays;
    const schoolDays = childData.schoolDays;
    const credits = allDays.filter((item: any) => item.substitutionRemaining && moment(item.date).isBetween(moment(date).subtract(schoolDays, 'days'), moment(date).add(schoolDays, 'days')) && moment(item.date).isBefore(moment(date),'day')).map((item: any) => {
      return item.substitutionRemaining;
    }).reduce(handleSum, 0);
    return credits;
  }, [substitutionsData.childData.allDays, substitutionsData.childData.schoolDays]);

  const getDaySubstitutionData = (date: any) => {
    const isExist = substitutionsData.list.filter((item: any) => item.childID === curChild.childID).length > 0 ? substitutionsData.list.find((item: any) => item.childID === curChild.childID).dates.filter((item: any) => item.date === moment(date).format("YYYY-MM-DD")).length === 1 : false;
    if(isExist) {
      const substitutionData = substitutionsData.list.find((item: any) => item.childID === curChild.childID).dates.find((item: any) => item.date === moment(date).format("YYYY-MM-DD"));
      return substitutionData;
    } else {
      return [];
    }
  };

  const getDayWatchdogData = (date: any) => {
    const isExist = substitutionsData.watchdogs.filter((item: any) => item.childID === curChild.childID).length > 0 ? substitutionsData.watchdogs.find((item: any) => item.childID === curChild.childID).dates.filter((item: any) => item.date === moment(date).format("YYYY-MM-DD")).length === 1 : false;
    if(isExist) {
      const watchdogData = substitutionsData.watchdogs.find((item: any) => item.childID === curChild.childID).dates.find((item: any) => item.date === moment(date).format("YYYY-MM-DD"));
      return watchdogData;
    } else {
      return [];
    }
  };

  const getConditionState = (condition: any) => {
    if(state.selectedDate) {
      if(condition === "past") {
        const date = moment(state.selectedDate);
        if(moment(date).isSameOrAfter(moment(),"date")) {
          return "green";
        } else {
          return "red";
        }
      } else if(condition === "school_enabled") {
        const schoolSettings = userData.schoolSettings;
        const modules = getSchoolSettings(schoolID, 'modules', schoolSettings) === null ? false : getSchoolSettings(schoolID, 'modules', schoolSettings);
        if(modules) {
          const substitutions = modules.substitutions ? modules.substitutions : false;
          if(substitutions) {
            const isEnabled = substitutions.enable ? substitutions.enable : false;
            if(isEnabled) {
              return "green";
            } else {
              return "red";
            }
          } else {
            return "grey";
          }
        } else {
          return "grey";
        }
      } else if(condition === "school_workday") {
        const workingDays = childData.workingDays;
        const day = parseInt(parseDay(moment(state.selectedDate).format("d")));
        if(workingDays.includes(day)) {
          return "green";
        } else {
          return "red";
        }
      } else if(condition === "school_holiday") {
        const holidays = childData.holidayDates;
        const date = moment(state.selectedDate).format("YYYY-MM-DD");
        if(!holidays.includes(date)) {
          return "green";
        } else {
          return "red";
        }
      } else if(condition === "children_attendance") {
        const attendance = curChild.attendanceProgram;
        if(attendance) {
          return "green";
        } else {
          return "red";
        }
      } else if(condition === "children_excuse_note") {
        const excuseNotes = childData.allDates;
        const date = moment(state.selectedDate).format("YYYY-MM-DD");
        if(!excuseNotes.includes(date)) {
          return "green";
        } else {
          return "red";
        }
      } else if(condition === "children_substitution") {
        const date = moment(state.selectedDate);
        const substitutionData = getDaySubstitutionData(date);
        if(substitutionData.length === 0) {
          return "green";
        } else {
          return "red";
        }
      } else if(condition === "children_watchdog") {
        const date = moment(state.selectedDate);
        const watchdogData = getDayWatchdogData(date);
        if(watchdogData.length === 0) {
          return "green";
        } else {
          return "red";
        }
      } else if(condition === "children_attendance_full_day") {
        const attendanceProgram = childData.attendanceProgram;
        if(attendanceProgram) {
          const days = attendanceProgram.days;
          if(days) {
            const day = parseInt(parseDay(moment(state.selectedDate).format("d")));
            const dayInfo = days.filter((item: any) => item.day === day).length === 1 ? days.find((item: any) => item.day === day) : false;
            if(dayInfo) {
              if(dayInfo.attendanceType !== "all") {
                return "green";
              } else {
                return "red";
              }
            } else {
              return "green";
            }
          } else {
            return "grey";
          }
        } else {
          return "grey";
        }
      } else if(condition === "children_credit") {
        const date = moment(state.selectedDate);
        const credit = getDateCredit(date);
        if(credit > 0) {
          return "green";
        } else {
          return "red";
        }
      } else {
        return "grey";
      }
    } else {
      return "grey";
    }
  };

  const getConditionIcon = (status: any) => {
    const icons: any = {
      grey: "circle",
      green: "checkmark",
      red: "close",
    };
    return icons[status];
  };

  return (
    <>
      <div className={classes.row}/>
      <div className={classes.row}>
        <div className={classes.block}>
          <span className={classes.title}>{t('substitution_date')}</span>
          <div className={classes.text}>
            <p>{htmlParse(t('substitution_date_description', {child: getChildData(curChild.childID).displayName}))}</p>
            <DatePicker presetDate={state.selectedDate} setDate={handleDate} presetDateRange={[startDate, lastDate]} allowReset={false}/>
            {
              state.selectedDate ? (
                <p>{htmlParse(t('substitution_available_credit_description', {count: getDateCredit(state.selectedDate), credit: inflection(getDateCredit(state.selectedDate), t('credit'), t('credits'), t('credits_more')).toLowerCase()}))}</p>
              ) : null
            }
          </div>
        </div>
        <div className={classes.block}>
          <span className={classes.title}>
            {t('substitutions_validation_rules')}
          </span>
          <div className={classes.text}>
            <ul>
              <li className={getConditionState("past")}>
                <SVG src={getConditionIcon(getConditionState("past"))}/>
                {t('substitutions_rules_date_past')}
              </li>
              <li className={getConditionState("school_enabled")}>
                <SVG src={getConditionIcon(getConditionState("school_enabled"))}/>
                {t('substitutions_rules_schools_enabled')}
              </li>
              <li className={getConditionState("school_workday")}>
                <SVG src={getConditionIcon(getConditionState("school_workday"))}/>
                {t('substitutions_rules_schools_workdays')}
              </li>
              <li className={getConditionState("school_holiday")}>
                <SVG src={getConditionIcon(getConditionState("school_holiday"))}/>
                {t('substitutions_rules_schools_holidays')}
              </li>
              <li className={getConditionState("children_attendance")}>
                <SVG src={getConditionIcon(getConditionState("children_attendance"))}/>
                {t('substitutions_rules_children_attendance')}
              </li>
              <li className={getConditionState("children_excuse_note")}>
                <SVG src={getConditionIcon(getConditionState("children_excuse_note"))}/>
                {t('substitutions_rules_children_excuse_notes')}
              </li>
              <li className={getConditionState("children_substitution")}>
                <SVG src={getConditionIcon(getConditionState("children_substitution"))}/>
                {t('substitutions_rules_children_substitutions')}
              </li>
              {
                getUserRole(userData.userObject.roleType) === "parent" ? (
                  <li className={getConditionState("children_watchdog")}>
                    <SVG src={getConditionIcon(getConditionState("children_watchdog"))}/>
                    {t('substitutions_rules_children_watchdogs')}
                  </li>
                ) : null
              }
              <li className={getConditionState("children_attendance_full_day")}>
                <SVG src={getConditionIcon(getConditionState("children_attendance_full_day"))}/>
                {t('substitutions_rules_children_attendance_full_day')}
              </li>
              <li className={getConditionState("children_credit")}>
                <SVG src={getConditionIcon(getConditionState("children_credit"))}/>
                {t('substitutions_rules_children_credit')}
              </li>
            </ul>  
          </div>
        </div>
      </div>
    </>
  );
};

export default SubstitutionsValidator;